<template>
  <v-col
    v-if="forwardSimulationsData"
    cols="12"
    class="px-0"
  >
    <block-item-card
      ref="forwardSimulations"
      :base-currency-symbol="baseCurrency ? baseCurrency.displaySymbol: ''"
      :export-element="isDashboard ? null : elementForwardSimulations"
      :title="isDashboard ? ' ' : $t('Risks.Section.MonteCarloSimulation.Title')"
      :title-large="false"
      :title-value="onePercentRisk"
      :title-value-color-red="true"
      :tooltip="isDashboard ? null : tooltip"
    >
      <template
        v-if="$vuetify.breakpoint.mdAndUp"
        #actions
      >
        <div
          v-if="isDashboard"
          class="title"
        >
          <span class="bullet">•</span>
          {{ $t('Dashboard.Banking.InterestRateRisk.RiskDistribution') }}
        </div>
        <v-spacer />
        <asset-button-group
          :items="assets"
          :selected.sync="selectedAsset"
          class="mt-4"
        />
        <button-text-toggle
          :icon="toggleChart === 'bar' ? 'mdi-chart-line-variant' : 'mdi-chart-bar'"
          class="mt-4 ml-2"
          @button-text-toggle-click="changeChart"
        />
      </template>
      <template
        v-else
        #actions-row
      >
        <v-card-title
          class="ml-3 pa-0"
          :class="`${aggregateItem ? 'mb-2' : 'mb-4'}`"
        >
          <asset-button-group
            :items="assets"
            :selected.sync="selectedAsset"
          >
            <template #extra>
              <button-text-toggle
                :height="36"
                :icon="toggleChart === 'bar' ? 'mdi-chart-line-variant' : 'mdi-chart-bar'"
                class="mx-3"
                @button-text-toggle-click="changeChart"
              />
            </template>
          </asset-button-group>
        </v-card-title>
        <v-card-title class="mx-3 mb-4 pa-0">
          <asset-button-group
            v-if="aggregateItem"
            :items="aggregateAssets"
            :selected.sync="selectedAggregateAsset"
          />
        </v-card-title>
      </template>

      <template
        v-if="$vuetify.breakpoint.mdAndUp"
        #avatar
      >
        <v-spacer />
        <asset-button-group-orange
          v-if="aggregateItem"
          :items="aggregateAssets"
          :selected.sync="selectedAggregateAsset"
          :mandatory="false"
          class="mt-3 mr-13"
          :select-max-width="200"
        />
      </template>

      <v-row class="ma-0 mb-5">
        <v-col
          class="ma-0"
          cols="12"
        >
          <v-row
            v-if="chartDataValues && chartDataValues.length"
            class="ma-0 flex-md-nowrap"
          >
            <v-col
              class="pa-0 flex-shrink-1"
              cols="12"
              lg="9"
              md="8"
              xl="10"
            >
              <v-lazy
                :options="{threshold: .5}"
                transition="fade-transition"
              >
                <e-chart-nii-forward-simulations
                  :axis-x-boundary-gap="true"
                  :axis-y-tick-prefix="baseCurrency ? baseCurrency.displaySymbol: ''"
                  :bar-background-color="themeColorRGBA('synthesis-ui-grey-06', 0.1)"
                  :chart-data-options="chartDataValues"
                  :chart-height="350"
                  :chart-scale="true"
                  :graph-line-show="true"
                  :has-line-bell="false"
                  :is-bar-chart="isBarChart"
                  axis-x-label-value-format=""
                  axis-y-label-value-format="percent"
                  chart-animation-easing="quarticOut"
                  chart-ref-id="ForwardSimulations"
                />
              </v-lazy>
            </v-col>
            <v-col
              class="pa-0"
              cols="12"
              lg="3"
              md="4"
              style="min-width: 270px"
              xl="2"
            >
              <v-row
                class="ma-0"
              >
                <v-col
                  :class="[{'pl-sm-1': $vuetify.breakpoint.sm && !appToolbarExtended}]"
                  :sm="appToolbarExtended ? 12 : 6"
                  class="ma-0 pa-0"
                  cols="12"
                  md="12"
                >
                  <item-chart-legend
                    :key="`forward-simulations-legend-risk-five-pct`"
                    :background-color="themeColorRGBA('synthesis-ui-green-08')"
                    :base-currency-symbol="baseCurrency ? baseCurrency.displaySymbol: ''"
                    :icon-color-style="'margin-top: -8px'"
                    :icon-color="themeColorRGBA('synthesis-ui-green-02')"
                    :title="$t('Risks.Section.MonteCarloSimulation.Block.Distribution.AverageCashFlow.CurrentValue')"
                    :title-value="averageCashFlow"
                    :title-value-color="themeColorRGBA('synthesis-brand-dark-blue')"
                    :title-value-size="18"
                    :tooltip="$t('Risks.Section.MonteCarloSimulation.Block.Distribution.AverageCashFlow.InfoTip')"
                    class="pa-5 pt-6"
                    style="min-width: 250px;min-height: 70px;height: 70px"
                  />
                </v-col>
                <v-col
                  :class="[{'pr-sm-1': $vuetify.breakpoint.sm && !appToolbarExtended}]"
                  :sm="appToolbarExtended ? 12 : 6"
                  class="ma-0 pa-0"
                  cols="12"
                  md="12"
                >
                  <item-chart-legend
                    :key="`forward-simulations-legend-risk-one-pct`"
                    :background-color="themeColorRGBA('synthesis-ui-orange-08')"
                    :base-currency-symbol="baseCurrency ? baseCurrency.displaySymbol: ''"
                    :icon-color="themeColorRGBA('synthesis-ui-orange-02')"
                    :icon-color-style="'margin-top: -8px'"
                    :title="$t('Risks.Section.MonteCarloSimulation.Block.Distribution.Risk_Pct_1.AdverseMarkets')"
                    :title-value="onePercentileRisk"
                    :title-value-color="themeColorRGBA('synthesis-brand-dark-blue')"
                    :title-value-size="18"
                    :tooltip="$t('Risks.Section.MonteCarloSimulation.Block.Distribution.Risk_Pct_1.InfoTip')"
                    class="pa-5 pt-6"
                    style="min-width: 250px;min-height: 70px;height: 70px"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </block-item-card>
  </v-col>
</template>

<script>

import chartsColorMixin               from '@/theme/default/components/charts/mixins/chartsColorMixin'
import API                            from '@/api/Api'
import { clone }                      from '@/lib/utils/helper'
import NIIForwardSimulationsResource from '@/api/resources/risks/NIIForwardSimulationsResource'
import AssetButtonGroup               from '@/theme/default/components/common/AssetButtonGroup'
import BlockItemCard                  from '@/theme/default/components/common/BlockItemCard'
import ItemChartLegend                from '@/theme/default/components/common/ItemChartLegend'
import EChartNiiForwardSimulations       from '@/theme/default/components/charts/echarts/EChartNiiForwardSimulations'
import ButtonTextToggle               from '@/theme/default/components/common/ButtonTextToggle'
import AssetButtonGroupOrange         from '@/theme/default/components/common/AssetButtonGroupOrange.vue'

export default {
  name      : 'ForwardSimulations',
  components: {
    AssetButtonGroupOrange,
    ButtonTextToggle,
    EChartNiiForwardSimulations,
    ItemChartLegend,
    BlockItemCard,
    AssetButtonGroup
  },
  directives: {},
  mixins    : [chartsColorMixin],
  props     : {
    isDashboard: {
      type   : Boolean,
      default: false
    }
  },
  dataStore: {
    appToolbarExtended       : 'App.Config.AppToolbarExtended',
    niiForwardSimulationsData: 'NII.FindSimulationData',
    niiBaseCurrencyData      : 'NII.FindSummaryData.Data',
    selectedPeriod           : 'NII.SelectedPeriod',
    currentAnalysisId        : 'ActiveAnalysis.currentAnalysisId'
  },
  enums    : {},
  dataModel: null,
  data () {
    return {
      toggleChart              : 'bar',
      selectedAsset            : 'net interest income',
      selectedAggregateAsset   : null,
      elementForwardSimulations: null,
      originalApiData          : null
    }
  },
  computed: {
    dates () {
      const ds = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
      return this.selectedPeriod === 'risk3mAnalysisData' ? ds.slice(0, 4) : ds
    },
    baseCurrency () {
      return this.niiBaseCurrencyData?.NIIBaseCurrency || null
    },

    isBarChart () {
      return (this.toggleChart === 'bar')
    },

    tooltip () {
      return this.isBarChart ? this.$t('Risks.Section.MonteCarloSimulation.InfoTip') : this.$t('Risks.Section.MonteCarloSimulation.Block.SimulatedPaths.InfoTip')
    },

    forwardSimulations () {
      return this.niiForwardSimulationsData?.Data || null
    },

    assets () {
      return this.forwardSimulations?.Assets || []
    },

    forwardSimulationsData () {
      return this.forwardSimulations?.Data || null
    },

    selectedItem () {
      return this.forwardSimulationsData?.find(item => item.AssetValue === this.selectedAsset) || null
    },

    aggregateItem () {
      return this.selectedItem?.AggregateItem || false
    },

    aggregateAssets () {
      return this.selectedItem?.PerCategoryCodeNames || []
    },

    selectedAggregateItem () {
      return this.selectedItem?.PerCategoryItems?.find(item => item.AssetValue === this.selectedAggregateAsset) || null
    },

    selectedFinalItem () {
      return this.aggregateItem && this.selectedAggregateItem ? this.selectedAggregateItem : this.selectedItem
    },

    onePercentRisk () {
      return this.selectedFinalItem?.AssetData[0]?.OnePercentRiskFmt || ''
    },

    onePercentileRisk () {
      return this.selectedFinalItem?.AssetData[0]?.OnePercentileFmt || ''
    },

    averageCashFlow () {
      return this.selectedFinalItem?.AssetData[0]?.MeanFmt || ''
    },

    distributionChart () {
      return this.selectedFinalItem?.AssetData[0].Values || []
    },

    monteCarloChart () {
      return [{
        dates : this.dates,
        values: this.selectedFinalItem?.CashFlowPathsValues || []
      }]
    },

    chartDataValues () {
      if (this.isBarChart) {
        return this.distributionChart
      } else {
        return this.monteCarloChart
      }
    },
    accountId () {
      return this.user?.selectedAccountId || null
    }

  },
  watch: {
    selectedAsset () {
      this.setSelectedAssetAggregate()
    }
  },
  beforeCreate () {},
  created () {
    this.getData()
    this.$bus.$on('nii:period-changed', this.onPeriodChanged)
    this.$bus.$on('nii:cancel-assumptions-edit', this.getData)
  },
  beforeDestroy () {
    this.$bus.$off('nii:period-changed', this.onPeriodChanged)
    this.$bus.$off('nii:cancel-assumptions-edit', this.getData)
  },
  beforeMount () {},
  mounted () {
    this.$nextTick(() => {
      this.elementForwardSimulations = this.$refs?.forwardSimulations?.$el
    })
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {
    getData () {
      API.Resource.NII.FindSimulationData(this.accountId, this.currentAnalysisId)
        .then(response => {
          this.handleResponse(response)
        })
        .catch(e => {
          this.handleResponse(e.response)
        })
    },
    handleResponse (response) {
      const data = API.responseData(response)
      const errors = API.responseErrors(response) || []
      const result = data?.findNiiAnalysis || null

      if (API.isResponseSuccess(response) && result) {
        // Store the original API data for period switching
        this.originalApiData = clone(result)

        const o = clone(result)
        o.selectedPeriod = this.selectedPeriod
        const v = new NIIForwardSimulationsResource(o)

        this.$set(this.niiForwardSimulationsData, 'Data', v)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    changeChart () {
      this.toggleChart = this.toggleChart === 'bar' ? 'line' : 'bar'
    },
    setSelectedAssetAggregate () {
      this.selectedAggregateAsset = ''
    },

    onPeriodChanged (newPeriod) {
      // Recreate the resource with the new selectedPeriod from original API data
      if (this.originalApiData) {
        const o = clone(this.originalApiData)
        o.selectedPeriod = newPeriod
        const v = new NIIForwardSimulationsResource(o)
        this.$set(this.niiForwardSimulationsData, 'Data', v)
      }
    }
  }
}
</script>

<style scoped>
.title {
  display: flex;
  align-items: center;
  font-family: Inter, Aeonik, Roboto, sans-serif !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  line-height: 20px !important;
  margin: 0;
  color: var(--v-synthesis-ui-grey-01-base) !important;
  margin: 20px 0;
}

.bullet {
  margin-right: 8px;
  color: var(--v-synthesis-ui-grey-01-base) !important;
}
</style>
